import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bcc/components/ui/dialog";
import type { ApprovalStatusResponseRecord } from "@shared/services/salesforce/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PENDING_REQUEST_COLUMNS,
  setPendingRequestsModalCellTestId,
  setPendingRequestsModalReadMoreTestId,
  setPendingRequestsModalRowTestId,
  setPendingRequestsModalTestId,
  type PendingRequestColumn,
} from "../products-table.utils";
import {
  formatChangeLabel,
  getActionTypeLabelKey,
  getApprovalStatusLabelKey,
  getChangeValueLabelKey,
  getFieldLabelKey,
  hasMultipleCountries,
  isLongComment,
  NO_VALUE_PLACEHOLDER,
  parseEffectiveDate,
  parseHistoryPayload,
  type PendingRequestChange,
} from "./pending-request-details";

interface PendingRequestsModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly requests: ApprovalStatusResponseRecord[];
}

type TranslatableValueKey = NonNullable<
  ReturnType<typeof getActionTypeLabelKey | typeof getApprovalStatusLabelKey | typeof getChangeValueLabelKey>
>;
type TranslateFn = ReturnType<typeof useTranslation>["t"];

// Salesforce owns these values, so one we have no translation for is shown raw rather than blank.
const translateOrRaw = (t: TranslateFn, key: TranslatableValueKey | undefined, raw: string): string => (key ? t(key) : raw);

const HEADER_CLASS = "pb-2 pr-4 text-left font-medium text-muted-foreground";

export const PendingRequestsModal = ({ open, onOpenChange, requests }: PendingRequestsModalProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent testId={setPendingRequestsModalTestId()} className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{t("views.productsTable.requests.modal.title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("views.productsTable.requests.modal.title")}</DialogDescription>
        </DialogHeader>
        {requests.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">{t("views.productsTable.requests.modal.empty")}</p>
        ) : (
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className={HEADER_CLASS}>{t("views.productsTable.requests.modal.requestId")}</th>
                  <th className={HEADER_CLASS}>{t("views.productsTable.requests.modal.actionType")}</th>
                  <th className={HEADER_CLASS}>{t("views.productsTable.requests.modal.status")}</th>
                  {PENDING_REQUEST_COLUMNS.map((column) => (
                    <th key={column} className={HEADER_CLASS}>
                      {t(`views.productsTable.requests.modal.${column}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.map((request, index) => (
                  <PendingRequestRow key={request.Id} request={request} index={index} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PendingRequestRow = ({ request, index }: { request: ApprovalStatusResponseRecord; index: number }) => {
  const { t } = useTranslation();
  const effectiveDate = parseEffectiveDate(request.Actual_Effective_Date__c);

  return (
    <tr data-testid={setPendingRequestsModalRowTestId(index)} className="border-b align-top last:border-0">
      <td className="py-3 pr-4 font-mono text-xs">{request.Name}</td>
      <td className="py-3 pr-4">{translateOrRaw(t, getActionTypeLabelKey(request.Performed_Action__c), request.Performed_Action__c)}</td>
      <td className="py-3 pr-4">
        <ApprovalStatusBadge status={request.Approval_status__c} />
      </td>
      <td className="py-3 pr-4" data-testid={setPendingRequestsModalCellTestId(index, "fieldsChanged")}>
        <FieldsChanged changes={parseHistoryPayload(request.History_Payload__c)} />
      </td>
      <td className="py-3 pr-4 whitespace-nowrap" data-testid={setPendingRequestsModalCellTestId(index, "effectiveDate")}>
        {effectiveDate ? t("dates.short", { date: effectiveDate }) : NO_VALUE_PLACEHOLDER}
      </td>
      <td className="max-w-64 py-3 pr-4" data-testid={setPendingRequestsModalCellTestId(index, "roasterComments")}>
        <ExpandableComment comment={request.Roaster_Comments__c} index={index} column="roasterComments" />
      </td>
      <td className="max-w-64 py-3" data-testid={setPendingRequestsModalCellTestId(index, "bmComments")}>
        <ExpandableComment comment={request.Comments__c} index={index} column="bmComments" />
      </td>
    </tr>
  );
};

const FieldsChanged = ({ changes }: { changes: PendingRequestChange[] }) => {
  const { t } = useTranslation();
  if (changes.length === 0) return <>{NO_VALUE_PLACEHOLDER}</>;
  const showCountry = hasMultipleCountries(changes);

  return (
    <ul className="space-y-1">
      {changes.map((change) => {
        // Salesforce can send a field we have no translation for; its raw name beats a blank label.
        const labelKey = getFieldLabelKey(change.fieldName);
        const label: string = labelKey ? t(labelKey) : change.fieldName;
        return (
          <li key={`${change.countryCode}-${change.fieldName}`} className="whitespace-nowrap">
            {formatChangeLabel(label, change.countryCode, showCountry)}:{" "}
            {translateOrRaw(t, getChangeValueLabelKey(change.fieldName, change.oldValue), change.oldValue)}{" "}
            <span className="text-green-600 dark:text-green-400">→</span>{" "}
            {translateOrRaw(t, getChangeValueLabelKey(change.fieldName, change.newValue), change.newValue)}
          </li>
        );
      })}
    </ul>
  );
};

interface ExpandableCommentProps {
  readonly comment: string | null | undefined;
  readonly index: number;
  readonly column: PendingRequestColumn;
}

const ExpandableComment = ({ comment, index, column }: ExpandableCommentProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const text = comment?.trim();
  if (!text) return <>{NO_VALUE_PLACEHOLDER}</>;
  const collapsible = isLongComment(text);

  return (
    <div>
      <p className={`break-words whitespace-pre-wrap ${collapsible && !expanded ? "line-clamp-3" : ""}`}>{text}</p>
      {collapsible && (
        <button
          type="button"
          className="mt-1 cursor-pointer text-xs font-medium text-primary hover:underline"
          aria-expanded={expanded}
          data-testid={setPendingRequestsModalReadMoreTestId(index, column)}
          onClick={() => setExpanded((previous) => !previous)}
        >
          {expanded ? t("views.productsTable.requests.modal.readLess") : t("views.productsTable.requests.modal.readMore")}
        </button>
      )}
    </div>
  );
};

const ApprovalStatusBadge = ({ status }: { status: string }) => {
  const { t } = useTranslation();
  const isApproved = status === "Approved";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        isApproved ? "bg-green-500/10 text-green-700 dark:text-green-400" : "bg-blue-500/10 text-blue-700 dark:text-blue-400"
      }`}
    >
      {translateOrRaw(t, getApprovalStatusLabelKey(status), status)}
    </span>
  );
};
